import { PortableTextBlock } from '@portabletext/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import clsx from 'clsx';
import { CSSProperties, ReactNode, createContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import BtnAccessibility from 'components/BtnAccessibility';
import { ScrollUpButton } from 'components/ScrollUpButton';

import { IQueryParams, ITrackEventProps } from 'typescriptDefs';
import { TAfAdsets } from 'typescriptDefs/afTracking';
import { TNavSettings } from 'typescriptDefs/layout';

import { afAdsetsPages, afChannels } from 'helpers/consts';
import { useScrollDepth, useScrollY, useTrackScrollDepth } from 'helpers/useScrollDepth';
import { usePageView } from 'utils/hooks/use-page-view';
import { SendUserToAppProvider, TSendUserToAppContext } from 'utils/hooks/use-send-user-to-app-context';
import { TLocale, defaultLang, getLocaleAndBasePath } from 'utils/i18n';

import { NavMobileHeight } from 'styles/tw-themes/spaceValues';

import Footer from './Footer';
import Navbar from './Navbar';
import { SmartBanner } from './SmartBanner';

export const LocaleContext = createContext<TLocale>(defaultLang);
const queryClient = new QueryClient();

type Props = {
  afAdset?: TAfAdsets;
  children: ReactNode;
  className?: string;
  containerClassname?: string;
  ctaToNavbar?: string;
  displayFooter?: boolean;
  displayNav?: boolean;
  displaySmartBanner?: boolean;
  footerDisclaimer?: PortableTextBlock | PortableTextBlock[];
  location: any;
  navSettings?: TNavSettings;
  oldTheme?: boolean;
  pageIsMultilingual: boolean;
  pageName: string;
  pageVariant?: string;
  pageViewProperties?: Record<string, unknown>;
  referralCode?: string;
  sendUserToAppContext?: TSendUserToAppContext;
  skipPageView?: boolean;
  trackScrollDepth?: boolean;
  trackScrollDepthProps?: Partial<ITrackEventProps>;
};

export const Layout = ({
  afAdset,
  children,
  className,
  containerClassname,
  ctaToNavbar,
  displayFooter = true,
  displayNav = true,
  displaySmartBanner = true,
  footerDisclaimer,
  location,
  navSettings,
  oldTheme = false,
  pageIsMultilingual,
  pageName,
  pageVariant,
  pageViewProperties,
  referralCode,
  sendUserToAppContext = { intent: '', sendUserToApp: false, oneLink: '', name: '' },
  skipPageView = false,
  trackScrollDepth = true,
  trackScrollDepthProps,
}: Props) => {
  const locale: TLocale = location ? getLocaleAndBasePath(location.pathname).locale : 'en';

  const queryParams: IQueryParams = {
    hash: referralCode ? { referral: referralCode } : {},
    search: {
      af_channel: afChannels.general,
      af_adset: afAdset ? afAdset : afAdsetsPages[location.pathname],
    },
  };

  const [showBannerBasic, setShowBannerBasic] = useState<boolean>(true);

  usePageView({ pageName, properties: pageViewProperties, skip: skipPageView });

  useEffect(() => {
    if (!displaySmartBanner || bannerBasicLocationBlacklist[location.pathname]) {
      setShowBannerBasic(false);
    }
  }, [displaySmartBanner, location.pathname]);

  const { refMain, scrollDepth } = useTrackPageScrolling({
    locale,
    pageName,
    pageVariant,
    trackScrollDepth,
    trackScrollDepthProps,
  });

  return (
    <LocaleContext.Provider value={locale || defaultLang}>
      <SendUserToAppProvider {...sendUserToAppContext}>
        <QueryClientProvider client={queryClient}>
          <div
            data-theme="light"
            className={twMerge(
              clsx(
                'flex min-h-[100vh] flex-col overflow-x-hidden font-basel-grotesk text-grey-500',
                containerClassname,
              ),
            )}
          >
            {displayNav && (
              <Navbar
                darkBg={navSettings?.darkBg}
                pageProps={{ locale, location, pageIsMultilingual, pageName, queryParams, variant: pageVariant }}
                ctaToNavbar={ctaToNavbar}
                banner={navSettings?.banner}
              />
            )}
            <main
              ref={refMain}
              className={twMerge(clsx('relative z-belowNav mb-auto', className))}
              data-theme={oldTheme ? 'legacy' : 'light'}
            >
              <div
                className={twMerge(
                  clsx(
                    'h-[var(--layout-top-mobile)] bg-white lg:h-navFullMinimizedHeight',
                    navSettings?.darkBg && 'bg-primary-500',
                    className,
                  ),
                )}
                style={
                  {
                    '--layout-top-mobile': `${NavMobileHeight + (navSettings?.banner ? 60 : 0)}px`,
                  } as CSSProperties
                }
              />
              {showBannerBasic && scrollDepth['0.25'] && <SmartBanner queryParams={queryParams} newHomepage={false} />}
              {children}
              <ScrollUpButton />
              <BtnAccessibility />
            </main>
            {displayFooter && <Footer pageDisclaimer={footerDisclaimer} pageName={pageName} location={location} />}
          </div>
        </QueryClientProvider>
      </SendUserToAppProvider>
    </LocaleContext.Provider>
  );
};

const bannerBasicLocationBlacklist = {
  '/cover/creditline': '/cover/creditline',
  '/cover/creditline/': '/cover/creditline/',
  '/cover/overdraft': '/cover/overdraft',
  '/cover/overdraft/': '/cover/overdraft/',
};

const useTrackPageScrolling = ({
  locale,
  pageName,
  pageVariant,
  trackScrollDepth,
  trackScrollDepthProps,
}: {
  locale: TLocale;
  pageName: string;
  pageVariant?: string;
  trackScrollDepth: boolean;
  trackScrollDepthProps?: Partial<ITrackEventProps>;
}) => {
  const refMain = useRef<HTMLElement>(null);
  const scrollY = useScrollY({ trackScrollDepth });
  const [mainHeight, setMainHeight] = useState<number>(0);

  useEffect(() => {
    if (refMain.current) {
      const refMainClientRect = refMain.current.getBoundingClientRect();
      setMainHeight(refMainClientRect.height);
    }
  }, [refMain, scrollY]);

  const scrollDepth = useScrollDepth({
    elementHeight: mainHeight,
    scrollY,
    trackScrollDepth,
  });

  useTrackScrollDepth({ locale, pageName, pageVariant, scrollDepth, trackScrollDepthProps });

  return {
    refMain,
    scrollDepth,
  };
};
